import { mapState, mapGetters } from 'vuex';
import Sortable from 'sortablejs';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';

// viewConfig is unvalidated server-side; anything but an array of objects is treated as malformed
const wellFormedSearchItems = view => {
  const searchItems = get(view, 'viewConfig.searchItems');
  return Array.isArray(searchItems) && searchItems.every(isPlainObject) ? searchItems : null;
};
export default {
  name: 'ViewVirtualDevicesViews',
  data: function () {
    return {
      showCreateDialog: false,
      showDeletePrompt: false,
      editItem: {},
      deleteViewId: null,
      selectedAccountIds: [],
      deviceTypes: [],
      deviceDrivers: [],
      viewList: [],
      currentPageItems: [],
      deviceTemplates: [],
      fetchGeneration: 0,
      totalCount: -1,
      linkedAlarmReports: [],
      isLoadingAlarmReports: false,
      accounts: [],
      iteratorPage: 1,
      iteratorPerPage: 12,
      firstFetch: true,
      isFetchingList: true,
      isFetchingExecute: false
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'language', 'permissions']),
    ...mapGetters({
      tenantId: 'authentication/tenantId',
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled',
      sessionUserId: 'authentication/userId'
    }),
    canWriteViews() {
      return this.permissions['VirtualDevice.write'] || this.permissions['VirtualDevice.manage'];
    },
    canManageViews() {
      return this.permissions['VirtualDevice.manage'];
    },
    cardActions() {
      return [...(this.canWriteViews ? ['edit'] : []), ...(this.canManageViews ? ['delete'] : [])];
    },
    hasNoMonitoringViews() {
      return this.viewList.length === 0;
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    },
    hasLinkedAlarmReports() {
      return this.linkedAlarmReports.length > 0;
    },
    deletePromptTitle() {
      return this.hasLinkedAlarmReports ? this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.deletePrompt.linkedAlarmReportsTitle', {
        count: this.linkedAlarmReports.length
      }) : this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.deletePrompt.title');
    }
  },
  watch: {
    language() {
      this.viewList.map(view => {
        const searchItems = wellFormedSearchItems(view);
        if (searchItems) {
          view.tags = searchItems.map(item => this.createTag(item));
        }
      });
    },
    selectedAccountIds() {
      this.loadViews();
    }
  },
  created() {
    // legacy device types live in niota, which is not proxied without N1
    if (!this.isFeatureFlagEnabled('env_n1_enabled')) {
      return;
    }
    NiotaClassicApiService.deviceTypesGetPlain().then(response => {
      this.deviceTypes = response.data.devicetypes;
    }).catch(error => {
      console.error(error);
    });
  },
  async mounted() {
    const user = await ApiService.getUser(this, this.$store.state.authentication.user.id);
    const userDefaults = get(user, 'properties.userDefaults', {}) || {};
    this.iteratorPerPage = userDefaults.smartGroupsListItemsPerPage || parseInt(this.$store.state.app.smartGroupsPerPage) || 12;
  },
  methods: {
    get,
    async handleItemsPerPageChange(itemsPerPage) {
      this.iteratorPerPage = itemsPerPage;
      const data = {
        id: this.sessionUserId,
        properties: {
          userDefaults: {
            smartGroupsListItemsPerPage: itemsPerPage
          }
        }
      };
      await ApiService.updateUserPartial(data);
      // persist the user-selected items-per-page
      this.$store.commit('app/setSmartGroupsPerPage', itemsPerPage);
      this.fetchCardDataForPage();
    },
    handlePageChange(page) {
      this.iteratorPage = page;
      this.fetchCardDataForPage();
    },
    async fetchCardDataForPage() {
      this.isFetchingExecute = true;
      const start = (this.iteratorPage - 1) * this.iteratorPerPage;
      const end = start + this.iteratorPerPage;

      // explicit loading flag per card, so "never answered" is distinguishable
      // from "answered", rather than relying on the key being absent
      this.currentPageItems = this.viewList.slice(start, end).map(view => ({
        ...view,
        isFetchingExecute: true
      }));
      if (this.viewList.length === 0) {
        // in case there is nothing to fetch, set flags right here,
        // rather than waiting for the promises below
        this.firstFetch = false;
        this.isFetchingExecute = false;
        return;
      }

      // identifies this fetch so results arriving after a page change are dropped
      this.fetchGeneration += 1;
      const generation = this.fetchGeneration;
      try {
        await this.fetchDriverAndTemplateFilterMetadata(this.currentPageItems);
      } catch (error) {
        // metadata only feeds tag labels, so a failure here must not hold every card on its skeleton
        console.error(error);
      }

      // each card is applied on its own as soon as its request settles, so a slow
      // or failing view delays and damages only itself
      await Promise.all(this.currentPageItems.map(view => this.fetchViewExecute(view.id).then(data => {
        if (generation !== this.fetchGeneration) {
          return;
        }
        try {
          this.applyCardResult(view, data);
        } catch (error) {
          // one malformed view must never block its neighbours
          console.error(error);
        }
        // the first result is enough to leave the full-page skeleton grid
        this.firstFetch = false;
        this.isFetchingExecute = false;
      })));

      // forces a terminal state even if every request was discarded or returned nothing
      this.firstFetch = false;
      this.isFetchingExecute = false;
    },
    // replaces one card in place, matched by id rather than position, so a
    // reordered or re-sliced page cannot land a result on the wrong card
    applyCardResult(view, data) {
      const index = this.currentPageItems.findIndex(item => item.id === view.id);
      if (index === -1) {
        return;
      }
      this.$set(this.currentPageItems, index, this.buildCardItem(view, data));
    },
    buildCardItem(view, data) {
      const base = {
        ...view,
        isFetchingExecute: false,
        // flag for the skeleton-loaders
        account: (this.findAccountById(view.tenantId) || {}).name || '--',
        accountId: view.tenantId
      };
      const searchItems = wellFormedSearchItems(view);

      // a view with missing or malformed searchItems is accepted by the API but filters
      // wrongly or not at all, so execute's count is untrustworthy; show the error state
      if (data === undefined || !searchItems) {
        return {
          ...base,
          devices: '-',
          errors: '-',
          warnings: '-',
          tags: []
        };
      }
      let tags = [];
      try {
        tags = searchItems.map(item => this.createTag(item));
      } catch (error) {
        // a bad tag costs only the tags, never the counts
        console.error(error);
      }
      return {
        ...base,
        devices: data.total,
        errors: data.alert,
        warnings: data.warn,
        tags
      };
    },
    accountSelectionChanged(accounts) {
      this.selectedAccountIds = accounts.map(account => account.id);
    },
    accountsLoaded() {
      this.accounts = this.$store.state.app.n2Accounts.accounts;
    },
    findAccountById(id) {
      return this.accounts.find(account => account.id === id);
    },
    fetchAccounts() {
      return this.$store.dispatch('app/loadAccounts').then(accounts => {
        return this.accounts = accounts;
      });
    },
    fetchViewExecute(viewId) {
      return ApiService.virtualDeviceViewsExecute(this, viewId).then(response => {
        return response;
      }).catch(error => {
        console.error(error);
      });
    },
    createTag(item) {
      let condition = item.condition;
      let text = item.search;
      let variable = item.variable;
      if (variable === 'operationalStatus') {
        text = this.operationalStatus[text] || item.search;
      }
      if (['deviceType', 'deviceDriver'].includes(variable)) {
        const dataArray = this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback') ? this.deviceTypes : this.deviceDrivers;
        const matchedData = dataArray.find(type => type.id === parseInt(text, 10));
        if (matchedData) {
          text = matchedData.title;
        }
      }
      if (variable === 'deviceTemplate') {
        const matchedData = this.deviceTemplates.find(template => template.id === parseInt(text, 10));
        if (matchedData) {
          text = matchedData.title;
        }
      }
      return {
        type: variable,
        text: text,
        condition: condition
      };
    },
    async fetchDriverAndTemplateFilterMetadata(views) {
      const driverIds = new Set();
      const templateIds = new Set();
      views.forEach(view => {
        // a malformed view shows the error state, so it needs no labels
        const searchItems = wellFormedSearchItems(view);
        if (searchItems) {
          searchItems.forEach(item => {
            if (item.variable === 'deviceDriver') {
              const id = parseInt(item.search, 10);
              if (!this.deviceDrivers.find(d => d.id === id)) {
                driverIds.add(id);
              }
            }
            if (item.variable === 'deviceTemplate') {
              const id = parseInt(item.search, 10);
              if (!this.deviceTemplates.find(t => t.id === id)) {
                templateIds.add(id);
              }
            }
          });
        }
      });
      if (driverIds.size === 0 && templateIds.size === 0) {
        return;
      }
      const driverPromises = Array.from(driverIds).map(id => ApiService.virtualDeviceDrivers.fetchOneById(id).catch(() => null));
      const templatePromises = Array.from(templateIds).map(id => ApiService.deviceTemplateGetByIdProductive(id).catch(() => null));
      const allResults = await Promise.all([...driverPromises, ...templatePromises]);
      const drivers = allResults.slice(0, driverIds.size).filter(d => d !== null);
      const templates = allResults.slice(driverIds.size).filter(t => t !== null).map(t => ({
        id: t.id,
        title: t.title
      }));
      this.deviceDrivers = [...this.deviceDrivers, ...drivers];
      this.deviceTemplates = [...this.deviceTemplates, ...templates];
    },
    loadViews() {
      this.firstFetch = true;
      this.isFetchingList = true;
      ApiService.virtualDeviceViewsFetch(this, {
        tenantIds: this.selectedAccountIds
      }).then(response => {
        this.viewList = response;
        this.totalCount = this.viewList.length;
        this.deviceDrivers = [];
        this.deviceTemplates = [];
        this.fetchCardDataForPage();
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadMonitoringViewFailed'), 'error');
      }).finally(() => {
        this.isFetchingList = false;
      });
    },
    initDraggable() {
      const grid = document.querySelector('.view-card-grid');
      Sortable.create(grid, {
        handle: '.view-card-drag-handle',
        ghostClass: 'view-card-drag-placeholder',
        onEnd: ({
          newIndex,
          oldIndex
        }) => {
          console.info('move card from', oldIndex, 'to', newIndex);
        }
      });
    },
    handleDelete(id) {
      this.deleteViewId = id;
      this.showDeletePrompt = true;
      this.fetchLinkedAlarmReports();
    },
    handleEdit(id) {
      this.editItem = this.viewList.find(view => view.id === id);
      this.showCreateDialog = true;
    },
    handleConfirmedDelete() {
      ApiService.virtualDeviceViewDelete(this, this.deleteViewId).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewDelete failed
        console.error(error);
      }).finally(() => {
        this.showDeletePrompt = false;
      });
    },
    handleCreate(options) {
      ApiService.virtualDeviceViewCreate(this, options).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewCreate failed
        console.error(error);
      });
    },
    // if feature-flag `rd-6855` is disabled, there still is an EDIT view, rather than just create
    handleUpdate(options) {
      ApiService.virtualDeviceViewUpdate(this, options).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewUpdate failed
        console.error(error);
      });
    },
    async fetchLinkedAlarmReports() {
      this.linkedAlarmReports = [];
      let page = 0;
      const PAGE_SIZE = 100;
      let hasMore = true;
      this.isLoadingAlarmReports = true;
      try {
        while (hasMore) {
          const linkedAlarmReportsResult = await ApiService.fetchAlarmLogReports(this, page, PAGE_SIZE, [{
            variable: 'smartGroupId',
            search: this.deleteViewId
          }], {
            variable: 'id',
            direction: 'ASC'
          });
          page++;
          this.linkedAlarmReports.push(...linkedAlarmReportsResult.reports);
          if (linkedAlarmReportsResult.reports.length !== PAGE_SIZE) {
            hasMore = false;
          }
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadAlarmReportsFailed'), 'error');
      } finally {
        this.isLoadingAlarmReports = false;
      }
    }
  }
};